import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_DIFFICULT_PERSON,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";

import {
  getDifficultyLevel,
  transformDifficultPersonUserTraitsData,
} from "../../utils/assessment-utils";

import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import SendToFriendButton from "../../components/send-to-friend-button";
import ShareButton from "../../components/share-button";
import {
  getDifficultPersonImage,
  getEnneagramBannerImage,
} from "../../utils/utility-functions";

import ExpandableSection from "../../components/expandable-collapsible/component";

import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";

import { useTranslation } from "react-i18next";
import { CallToActionSection } from "../../components/call-to-action/component";
import InviteFriend from "../../components/invite-friend";
import PathHelper from "../../utils/path-helper";

const DifficultPersonReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,

  getGuestUserConnect,
  getTransactionDetailConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
}) => {
  // const [content, setContent] = useState(null);

  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const assessmentId = currentAssessmentAttemptData?.assessmentId;
  const { t } = useTranslation();

  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;

  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_DIFFICULT_PERSON[currentLocale] ||
            MODEL_TYPE_DIFFICULT_PERSON[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId && isAuth) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId, isAuth]);

  const bannerImage = getEnneagramBannerImage();
  const result1ImageText = "difficult-result-1";
  const result2ImageText = "difficult-result-2";
  const result1Image = getDifficultPersonImage(result1ImageText);
  const result2Image = getDifficultPersonImage(result2ImageText);

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
    pink: "from-pink-600 via-pink-500",
  };

  const colors = [
    "#eef4fe",
    "#f8f4fe",
    "#fdf6eb",
    "#ebfbeb",
    "#feeded",
    "#c7edf9",
    "#e7feeb",
    "#eff4fd",
    "#eae2fd",
  ];

  const descriptiveCardColorMap = [
    { bg: "bg-blue-50", text: "text-blue-800" },
    { bg: "bg-purple-50", text: "text-purple-800" },
    { bg: "bg-amber-50", text: "text-amber-800" },
    { bg: "bg-green-50", text: "text-green-800" },
    { bg: "bg-pink-50", text: "text-pink-800" },
    { bg: "bg-sky-50", text: "text-sky-800" },
    { bg: "bg-emerald-50", text: "text-emerald-800" },
    { bg: "bg-blue-50", text: "text-blue-800" },
    { bg: "bg-violet-50", text: "text-violet-800" },
  ];

  const formatSourceContent = (content) => {
    if (typeof content === "string") {
      if (content.startsWith("http://") || content.startsWith("https://")) {
        return (
          <a
            href={content}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#805fe6]"
          >
            {content}
          </a>
        );
      }
      return content;
    }
    return content;
  };

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        {/* Gradient Overlay */}
        <div
          className={`absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Content */}
        <div className="relative h-full flex items-center">
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12" id={reportType}>
            {/* Title & Button Wrapper */}
            <div
              className="flex flex-col  md:flex-row  items-start justify-between gap-4 pr-10 md:pr-16"
              id={reportType}
            >
              {/* Title */}
              <h2
                id={reportType}
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-4xl md:text-4xl font-bold text-white  break-words whitespace-pre-wrap text-left"
              >
                {title}
              </h2>

              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // const CallToActionSection = ({ onClick1, onClick2 }) => {
  //   return (
  //     <section className="space-y-6">
  //       <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
  //         <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
  //           <h3 className="text-xl font-bold text-white text-center">
  //             {t("report.adhd.testMessage")}
  //           </h3>
  //           <p className="text-white opacity-90 text-center">
  //             {t("report.adhd.discoverMessage")}
  //           </p>
  //           <TakeTestButton
  //             onClick={onClick1}
  //             id="difficult-person-report-personality-test-button"
  //           />
  //         </div>
  //       </div>
  //       <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
  //         <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
  //           <h3 className="text-xl font-bold text-white text-center">
  //             {t("report.adhd.bigFiveMessage")}
  //           </h3>
  //           <p className="text-white opacity-90 text-center">
  //             {t("report.adhd.strengthMesssge")}
  //           </p>
  //           <TakeTestButton
  //             onClick={onClick2}
  //             id="difficult-person-report-take-big-five-test-button"
  //           />
  //         </div>
  //       </div>
  //     </section>
  //   );
  // };

  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const Table = ({ averagePercentage, updatedOverallScore }) => (
    <div className="w-full bg-white">
      <table className="w-full table-auto">
        <thead>
          <tr className="border-b border-gray-300">
            <th className="px-2 py-2 text-left font-bold tracking-wider text-lg">
              {t("report.autism_spectrum.your_scores")}
            </th>
            <th className="px-2 py-2 text-right font-bold tracking-wider text-lg">
              {t("report.autism_spectrum.results")}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white">
          <tr className="border-b border-gray-300">
            <td className="px-2 py-2 text-sm font-medium text-gray-900">
              {contentData?.totalScore?.replace(/\{score\}/g, "")?.trim() || ""}
            </td>
            <td className="px-2 py-2 text-right text-sm font-semibold text-[#805fe6]">
              {averagePercentage}
            </td>
          </tr>
          <tr className="border-b border-gray-300">
            <td className="px-2 py-2 text-sm font-medium text-gray-900">
              {contentData?.totalScoreCategory
                ?.replace(/\{scoreCategory\}/g, "")
                ?.trim() || ""}
            </td>
            <td className="px-2 py-2 text-right text-sm font-semibold text-[#805fe6]">
              {updatedOverallScore?.Heading}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;
    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (isQuestion) {
      finalColor = "#805bec";
    } else if (isNumber) {
      finalColor = "#a98ff9";
    } else {
      finalColor = "#c8c9f4";
    }
    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
      >
        {/* Inner Purple Circle */}
        <circle cx={centerPoint} cy={centerPoint} r={40} fill={finalColor} />

        {/* Character/Text inside the Circle */}
        {!isBullet && (
          <text
            x={centerPoint}
            y={centerPoint + 4}
            fontSize={textSize}
            fontWeight="bold"
            fill="white"
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="Arial, sans-serif"
          >
            {character}
          </text>
        )}
      </svg>
    );
  };

  const RecommendationCard = ({ recommendations }) => {
    return (
      <div
        style={{ backgroundColor: "#eef4fe" }}
        className="bg-gray-100 p-4 md:p-6 rounded-2xl shadow-lg w-full max-w-2xl"
      >
        {/* Main Heading with responsive icon sizing */}
        <div className="flex items-start gap-2 mb-8">
          <div>
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 shrink-0 flex items-start justify-center rounded-full border-1 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                <CircleIcon character={"?"} size={30} />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                {recommendations?.RootHeading}
              </h2>
            </div>
          </div>
        </div>

        <ul className="mt-3 sm:mt-4 pl-4 sm:pl-10 space-y-3 sm:space-y-4">
          {recommendations?.Content?.map((section, index) => (
            <li key={index} className="text-sm text-gray-600">
              <div className="flex items-start gap-2">
                <div>
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] lg:mt-[5px] md:mt-[5px]">
                      <CircleIcon
                        character={(index + 1).toString()}
                        size={16}
                      />
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 leading-none">
                      {section?.Heading}
                    </h3>
                  </div>

                  {/* Content aligned to Heading */}
                  <ul className="mt-1 sm:mt-2 pl-6 sm:pl-10 space-y-1 sm:space-y-2">
                    {section.Content.map((item, subIndex) => (
                      <li
                        key={subIndex}
                        className="text-sm my-4 text-gray-600 flex items-start gap-2"
                      >
                        {/* Mobile-friendly bullet size */}
                        <div className="flex items-start gap-2">
                          <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] lg:mt-[5px] md:mt-[5px]">
                            <CircleIcon character={""} size={16} />
                          </div>
                          <p className="text-base sm:text-lg text-gray-700 leading-none">
                            {item}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const NextStepsCard = ({ nextSteps }) => {
    return (
      <div
        style={{ backgroundColor: "#e5ffea" }}
        className="bg-gray-100 p-4 md:p-6 rounded-2xl shadow-lg w-full max-w-2xl"
      >
        {/* Main Heading with responsive icon sizing */}
        <div className="flex items-start gap-2 mb-8">
          <div>
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 shrink-0 flex items-start justify-center rounded-full border-1 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                <CircleIcon character={"?"} size={30} />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                {nextSteps?.RootHeading}
              </h2>
            </div>
          </div>
        </div>

        <ul className="my-1 sm:my-2 pl-6 sm:pl-10 space-y-1 sm:space-y-2 ">
          {nextSteps?.Content?.map((section, index) => (
            <li
              key={index}
              className="text-sm my-4 text-gray-600 flex items-start gap-2"
            >
              <div className="flex items-start gap-2">
                <div>
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] lg:mt-[5px] md:mt-[5px]">
                      <CircleIcon
                        character={(index + 1).toString()}
                        size={16}
                      />
                    </div>
                    <p className="text-base sm:text-lg text-gray-700 leading-none">
                      {section}
                    </p>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <p className="text-base sm:text-lg text-gray-700 leading-none">
          {nextSteps?.ContentFollowUp}
        </p>
      </div>
    );
  };

  const updatedUserTraits = transformDifficultPersonUserTraitsData(userTraits);

  const toSnakeCase = (str) => str.toLowerCase().replace(/\s+/g, "_");

  const overallScore = contentData?.scoreExplanation?.Content;

  const subscaleScores = contentData?.subscaleScoresHeading;

  const traitInsights = contentData?.traitInsights;

  const recommendations = contentData?.recommendations;

  const nextSteps = contentData?.nextSteps;

  const transformedOverallScore = overallScore?.reduce((acc, item) => {
    const key = toSnakeCase(item?.EnglishCode);
    acc[key] = { Heading: item?.Heading, Content: item?.Content };
    return acc;
  }, {});

  const transformedSubscaleScores = subscaleScores?.Content?.reduce(
    (acc, item) => {
      const str = item?.EnglishCode?.split(":")[0].trim();
      const key = toSnakeCase(str);

      const content = item?.Content?.reduce((acc, item) => {
        const key = toSnakeCase(item?.EnglishCode);
        acc[key] = {
          Heading: item?.Heading,
          Content: item?.Content,
          EnglishCode: item?.EnglishCode,
        };
        return acc;
      }, {});
      acc[key] = {
        Heading: item?.RootHeading,
        Content: content,
        EnglishCode: item?.EnglishCode,
      };
      return acc;
    },
    {}
  );

  function generateUpdatedScores(userTraits, subscaleScores, overallScores) {
    let updatedSubscaleScore = {};
    let updatedOverallScore = {};
    if (!userTraits || typeof userTraits !== "object") {
      console.warn("Invalid or missing userTraits. Returning empty result.");
      return { updatedSubscaleScore, updatedOverallScore };
    }

    if (!subscaleScores || typeof subscaleScores !== "object") {
      console.warn(
        "Invalid or missing subscaleScores. Skipping subscale calculations."
      );
    }

    if (!overallScores || typeof overallScores !== "object") {
      console.warn(
        "Invalid or missing overallScores. Skipping overall score calculations."
      );
    }

    // Process subscale scores safely
    if (userTraits.result && typeof userTraits.result === "object") {
      for (let trait in userTraits.result) {
        if (!subscaleScores || !subscaleScores[trait]) {
          console.warn(`Subscale definition missing for: ${trait}`);
          continue;
        }

        const score = userTraits.result[trait]?.score;
        const difficultyLevel = getDifficultyLevel(score, "subscale");

        updatedSubscaleScore[trait] = {
          Heading: subscaleScores[trait].Heading?.replace(
            "{score}",
            "" ?? "N/A"
          ).replace(":", ""),
          DifficultyHeading:
            subscaleScores[trait].Content[difficultyLevel]?.Heading ||
            "Unknown Difficulty",
          Content:
            subscaleScores[trait].Content[difficultyLevel]?.Content ||
            "No content available.",
        };
      }
    } else {
      console.warn("Invalid or missing subscale scores in userTraits.");
    }

    // Process overall score safely
    const totalScore = userTraits.totalScore;
    if (typeof totalScore === "number" && overallScores) {
      const overallDifficultyLevel = getDifficultyLevel(totalScore, "overall");
      updatedOverallScore = {
        Heading:
          overallScores[overallDifficultyLevel]?.Heading ||
          "Unknown Overall Score",
        Content:
          overallScores[overallDifficultyLevel]?.Content ||
          "No content available.",
      };
    } else {
      console.warn("Invalid or missing totalScore in userTraits.");
    }

    return { updatedSubscaleScore, updatedOverallScore };
  }

  function getAveragePercentage(updatedUserTraits) {
    if (
      !updatedUserTraits ||
      typeof updatedUserTraits !== "object" ||
      !updatedUserTraits.result
    ) {
      console.warn("Invalid or missing updatedUserTraits.");
      return 0; // Default value if data is missing
    }

    let totalPercentage = 0;
    let count = 0;

    for (let trait in updatedUserTraits.result) {
      if (updatedUserTraits.result[trait]?.percentage !== undefined) {
        totalPercentage += updatedUserTraits.result[trait].percentage;
        count++;
      }
    }

    if (count === 0) {
      console.warn("No valid percentage data found.");
      return 0; // Avoid division by zero
    }

    return totalPercentage / count;
  }

  const { updatedSubscaleScore, updatedOverallScore } = generateUpdatedScores(
    updatedUserTraits,
    transformedSubscaleScores,
    transformedOverallScore
  );

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {/* Show Loader while loading */}
          {isLoading ? (
            <Loader isLoading={isLoading} />
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
                    <div className="p-4 md:px-8 md:py-6">
                      <p className="text-gray-700 mb-6">{contentData?.intro}</p>

                      {/* Table Section with better spacing */}
                      <div className="mb-8 -ml-4 md:-ml-8">
                        <Table
                          averagePercentage={updatedUserTraits?.totalScore?.toFixed(
                            1
                          )}
                          updatedOverallScore={updatedOverallScore}
                        />
                      </div>

                      {/* Score Explanation */}
                      <h2 className="text-xl font-semibold text-gray-700 mt-10 mb-5">
                        {contentData?.scoreExplanation?.RootHeading}
                      </h2>

                      <DescriptiveCard
                        heading={updatedOverallScore?.Heading}
                        overview={updatedOverallScore?.Content}
                        bgClr={"bg-blue-50"}
                        textClr={"text-blue-800"}
                      />
                    </div>

                    {/* Right Section: Image */}
                    <div className="p-4 md:px-8 md:py-6 flex items-center h-[90%] justify-center">
                      <img
                        src={result1Image}
                        alt="BannerImage"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* You can apply same pattern for the next sections */}
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={
                        subscaleScores?.RootHeading ? (
                          <>
                            <span className="block md:inline">
                              {(() => {
                                const [firstWord, ...rest] =
                                  subscaleScores.RootHeading.split(" ");
                                return (
                                  <>
                                    <span className="block md:inline">
                                      {firstWord}
                                    </span>{" "}
                                    <span className="block md:inline">
                                      {rest.join(" ")}
                                    </span>
                                  </>
                                );
                              })()}
                            </span>
                          </>
                        ) : null
                      }
                      color="purple"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 items-stretch">
                      {[
                        {
                          key: "assertiveness_and_aggression",
                          bgClr: "#fffeed",
                        },
                        {
                          key: "empathy_and_emotional_awareness",
                          bgClr: "#c3edfa",
                        },
                        {
                          key: "entitlement_and_superiority",
                          bgClr: "#eae1ff",
                        },
                        {
                          key: "manipulativeness_and_control",
                          bgClr: "#ffeded",
                        },
                      ].map((item, index) => (
                        <div key={item.key} className="flex flex-col h-full">
                          <h2 className="text-lg font-semibold text-gray-700 mt-6 mb-3">
                            {updatedSubscaleScore?.[item.key]?.Heading}
                          </h2>
                          <DescriptiveCard
                            heading={
                              <>
                                {updatedSubscaleScore?.[
                                  item.key
                                ]?.DifficultyHeading?.split(" ")
                                  .slice(0, 3)
                                  .join(" ")}
                                <br />
                                {updatedSubscaleScore?.[
                                  item.key
                                ]?.DifficultyHeading?.split(" ")
                                  .slice(3)
                                  .join(" ")}
                              </>
                            }
                            overview={updatedSubscaleScore?.[item.key]?.Content}
                            bgClr={
                              descriptiveCardColorMap[
                                index % descriptiveCardColorMap.length
                              ].bg
                            }
                            textClr={
                              descriptiveCardColorMap[
                                index % descriptiveCardColorMap.length
                              ].text
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={
                        traitInsights?.RootHeading ? (
                          <>
                            <span className="block md:inline">
                              {(() => {
                                const [firstWord, ...rest] =
                                  traitInsights.RootHeading.split(" ");
                                return (
                                  <>
                                    <span className="block md:inline">
                                      {firstWord}{" "}
                                    </span>
                                    <span className="block md:inline">
                                      {rest.join(" ")}
                                    </span>
                                  </>
                                );
                              })()}
                            </span>
                          </>
                        ) : null
                      }
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 items-stretch">
                      {traitInsights?.Content?.map((item, index) => (
                        <div key={index} className="flex flex-col h-full">
                          <DescriptiveCard
                            heading={item?.Heading}
                            overview={item?.Content}
                            bgClr={
                              descriptiveCardColorMap[
                                (descriptiveCardColorMap.length - index - 1) %
                                  descriptiveCardColorMap.length
                              ].bg
                            }
                            textClr={
                              descriptiveCardColorMap[
                                (descriptiveCardColorMap.length - index - 1) %
                                  descriptiveCardColorMap.length
                              ].text
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={t("report.narcissistic_personality.what_next")}
                      color="green"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-10 items-stretch">
                      {/* Recommendation Card */}
                      <div className="h-full flex flex-col">
                        <RecommendationCard recommendations={recommendations} />
                      </div>

                      {/* Image & Next Steps Card */}
                      <div className="flex flex-col h-full justify-between">
                        {/* Image Container */}
                        <div className="flex items-center justify-center mb-4 flex-shrink-0">
                          <img
                            src={result2Image}
                            alt="Banner Image 1"
                            className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
                          />
                        </div>

                        {/* Spacer to push NextStepsCard down */}
                        <div className="flex-1"></div>

                        {/* Next Steps Card */}
                        <div>
                          <NextStepsCard nextSteps={nextSteps} />
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              {isAuth ? (
                <CallToActionSection
                  currentLocale={currentLocale}
                  t={t}
                  getAssessmentConnect={getAssessmentConnect}
                  getAssessmentAttemptByUserIdConnect={
                    getAssessmentAttemptByUserIdConnect
                  }
                  assessmentId={assessmentId}
                  userId={userGUID}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.BLUE_INDIGO}
                />
              ) : (
                <CallToActionSection
                  onClick1={() => {
                    navigate(
                      `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                    );
                  }}
                  onClick2={() => {
                    navigate(
                      `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`
                    );
                  }}
                />
              )}

              {isAuth && (
                <InviteFriend
                  sectionMode={true}
                  getGuestUserConnect={getGuestUserConnect}
                  getTransactionDetailConnect={getTransactionDetailConnect}
                  getBillingConnect={getBillingConnect}
                  guestUserChangesConnect={guestUserChangesConnect}
                  isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
                  userId={userGUID}
                  userEmail={userData?.email}
                  buttonText={t("dashboard.invite_friend_cta")}
                  selectedPlan={selectedPlan}
                  assessmentData={currentAssessmentAttemptData}
                  showRemainingInvites={true}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
                />
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};
export default DifficultPersonReport;
